import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const SCHEMAS = {
  services: {
    title: "Services",
    fields: [
      { k: "title", t: "text", label: "Title" },
      { k: "slug", t: "text", label: "Slug (url)" },
      { k: "icon", t: "text", label: "Lucide icon name (e.g. Megaphone)" },
      { k: "short", t: "textarea", label: "Short description" },
      { k: "what", t: "textarea", label: "What it is" },
      { k: "who", t: "textarea", label: "Who it's for" },
      { k: "outcome", t: "textarea", label: "Expected outcome" },
      { k: "how", t: "list", label: "How it works (one step per line)" },
      { k: "requirements", t: "list", label: "Requirements (one per line)" },
      { k: "order", t: "number", label: "Display order" },
      { k: "featured", t: "bool", label: "Featured" },
    ],
    primary: "title", sub: "short",
  },
  pricing: {
    title: "Pricing Plans",
    fields: [
      { k: "name", t: "text", label: "Plan name" },
      { k: "tagline", t: "text", label: "Tagline" },
      { k: "price_label", t: "text", label: "Price label (e.g. Request Quote)" },
      { k: "features", t: "list", label: "Features (one per line)" },
      { k: "order", t: "number", label: "Display order" },
      { k: "highlighted", t: "bool", label: "Highlight as popular" },
    ],
    primary: "name", sub: "tagline",
  },
  faqs: {
    title: "FAQs",
    fields: [
      { k: "q", t: "text", label: "Question" },
      { k: "a", t: "textarea", label: "Answer" },
      { k: "category", t: "text", label: "Category" },
      { k: "order", t: "number", label: "Display order" },
    ],
    primary: "q", sub: "category",
  },
  testimonials: {
    title: "Testimonials",
    fields: [
      { k: "name", t: "text", label: "Name" },
      { k: "role", t: "text", label: "Role" },
      { k: "quote", t: "textarea", label: "Quote" },
      { k: "rating", t: "number", label: "Rating (1-5)" },
      { k: "order", t: "number", label: "Display order" },
      { k: "published", t: "bool", label: "Published" },
    ],
    primary: "name", sub: "role",
  },
};

export default function AdminContent() {
  const { pathname } = useLocation();
  const kind = pathname.split("/").pop();
  const schema = SCHEMAS[kind];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/admin/${kind}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  if (!schema) return <div className="text-slate-400">Unknown content type.</div>;

  const openNew = () => { const f = {}; schema.fields.forEach((x) => { f[x.k] = x.t === "bool" ? false : x.t === "number" ? 0 : x.t === "list" ? [] : ""; }); setForm(f); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it }); setEditId(it.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = { ...form };
    schema.fields.forEach((x) => { if (x.t === "number") payload[x.k] = Number(payload[x.k]) || 0; });
    try {
      if (editId) await api.put(`/admin/${kind}/${editId}`, payload);
      else await api.post(`/admin/${kind}`, payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed to save"); } finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/admin/${kind}/${id}`); toast.success("Deleted"); load(); };
  const set = (k, t) => (e) => {
    let v = e?.target ? e.target.value : e;
    if (t === "list") v = v.split("\n");
    setForm((f) => ({ ...f, [k]: v }));
  };

  return (
    <div>
      <SEO title={`${schema.title} | Admin`} />
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold tracking-tight">{schema.title}</h1><p className="text-slate-400 text-sm">{items.length} items · edits update the live site</p></div>
        <Button onClick={openNew} data-testid="content-new"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>

      <div className="mt-5 space-y-2">
        {items.map((it) => (
          <div key={it.id} data-testid={`content-item-${it.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            <GripVertical className="w-4 h-4 text-slate-600" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{it[schema.primary]}</p>
              <p className="text-xs text-slate-400 truncate">{it[schema.sub]}</p>
            </div>
            {typeof it.order !== "undefined" && <Badge className="bg-white/10 text-slate-300">#{it.order}</Badge>}
            {("published" in it) && <Badge className={it.published ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-slate-400"}>{it.published ? "Published" : "Draft"}</Badge>}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`content-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => del(it.id)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-slate-500 text-sm">No items yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} {schema.title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {schema.fields.map((x) => (
              <div key={x.k} className="space-y-1.5">
                {x.t !== "bool" && <Label>{x.label}</Label>}
                {x.t === "text" && <Input value={form[x.k] || ""} onChange={set(x.k)} data-testid={`field-${x.k}`} />}
                {x.t === "number" && <Input type="number" value={form[x.k] ?? 0} onChange={set(x.k)} />}
                {x.t === "textarea" && <Textarea rows={3} value={form[x.k] || ""} onChange={set(x.k)} />}
                {x.t === "list" && <Textarea rows={4} value={Array.isArray(form[x.k]) ? form[x.k].join("\n") : ""} onChange={set(x.k, "list")} />}
                {x.t === "bool" && <div className="flex items-center gap-3 pt-1"><Switch checked={!!form[x.k]} onCheckedChange={set(x.k)} data-testid={`field-${x.k}`} /><Label>{x.label}</Label></div>}
              </div>
            ))}
            <DialogFooter><Button type="submit" disabled={loading} data-testid="content-save">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
